import { useState, useEffect, useRef } from "react";

import { Globe, Loader2, AlertCircle } from "lucide-react";
import toast from "react-hot-toast";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import {
  customDomainsApi,
  CustomDomain,
  EntriConfig,
} from "@/lib/api/custom-domains";
import { toEntriRecords } from "@/lib/entri";
import { useEntri } from "@/hooks/use-entri";
import { analytics } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import { ENTRI_EVENTS } from "./entri-events";
import { DomainCard } from "./DomainCard";

/* --------------------------- input validation --------------------------- */

/** Strip protocol, path, whitespace, trailing dot; lowercase. */
function sanitizeDomain(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "")
    .replace(/\.$/, "");
}

const HOSTNAME_RE =
  /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/;

/** Sanitize + validate a subdomain. Returns the cleaned value and an error (if any). */
function validateDomain(raw: string): { value: string; error: string | null } {
  const value = sanitizeDomain(raw);
  if (!value) return { value, error: "Please enter a domain." };
  if (!HOSTNAME_RE.test(value)) {
    return { value, error: "Enter a valid domain like app.example.com." };
  }
  // Apex/root domains have only the registrable part (≤ 2 labels). We need a subdomain.
  if (value.split(".").length < 3) {
    return {
      value,
      error: "Enter a subdomain (e.g. app.example.com), not a root domain.",
    };
  }
  return { value, error: null };
}

interface CustomDomainModalProps {
  isOpen: boolean;
  projectId: string;
  onClose: () => void;
  onDomainsChange?: () => void;
  /** When true (non-embed users), offer Entri's one-click DNS connection. */
  allowEntri?: boolean;
}

export function CustomDomainModal({
  isOpen,
  projectId,
  onClose,
  onDomainsChange,
  allowEntri = false,
}: CustomDomainModalProps) {
  const [domains, setDomains] = useState<CustomDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState("");
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [autoCheckingDomains, setAutoCheckingDomains] = useState<Set<string>>(
    new Set(),
  );
  // Domains connected via Entri this session — DNS was written for the user, so
  // we hide the "Stop" link (activation is automatic + the webhook backs it up).
  const [entriDomains, setEntriDomains] = useState<Set<string>>(new Set());
  const [verifyingDomains, setVerifyingDomains] = useState<Set<string>>(
    new Set(),
  );
  const [deletingDomains, setDeletingDomains] = useState<Set<string>>(
    new Set(),
  );
  const [settingPrimary, setSettingPrimary] = useState<string | null>(null);
  const [entriConfig, setEntriConfig] = useState<EntriConfig | null>(null);
  // Which domain the user is connecting via Entri — the onSuccess window event
  // only carries the domain name, so we resolve the row from this ref.
  const connectingDomainRef = useRef<CustomDomain | null>(null);

  // Entri one-click DNS modal (entrijs). `entriActive` hides our Radix dialog
  // while Entri's iframe is up so its buttons stay clickable; the hook owns close
  // detection (onEntriClose event + an iframe-removal safety net).
  const { open: openEntri, isActive: entriActive } = useEntri({
    onSuccess: () => {
      const domain = connectingDomainRef.current;
      analytics.track(ENTRI_EVENTS.CONNECT_SUCCEEDED, {
        projectId,
        domain: domain?.domain,
      });
      toast.success("DNS configured! Verifying your domain…");
      if (domain) {
        setAutoCheckingDomains((prev) => new Set(prev).add(domain.id));
        setEntriDomains((prev) => new Set(prev).add(domain.id));
        handleVerify(domain.id);
      }
    },
  });

  useEffect(() => {
    if (isOpen) {
      loadDomains();
    }
  }, [projectId, isOpen]);

  // Fetch Entri config for non-embed users so we can offer one-click DNS setup.
  useEffect(() => {
    if (!isOpen || !allowEntri) {
      setEntriConfig(null);
      return;
    }
    let cancelled = false;
    customDomainsApi
      .getEntriConfig(projectId)
      .then((config) => {
        if (!cancelled) setEntriConfig(config);
      })
      .catch(() => {
        if (!cancelled) setEntriConfig({ enabled: false });
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, allowEntri, projectId]);

  const entriEnabled = allowEntri && entriConfig?.enabled === true;

  // Auto-polling for domains in verifying/pending/provisioning-SSL status
  useEffect(() => {
    if (autoCheckingDomains.size === 0) return;

    const interval = setInterval(async () => {
      for (const domainId of autoCheckingDomains) {
        try {
          const updated = await customDomainsApi.verify(projectId, domainId);
          setDomains((prev) =>
            prev.map((d) => (d.id === domainId ? updated : d)),
          );

          // Keep polling until BOTH the hostname and the SSL cert are active —
          // Cloudflare flips the hostname to "active" before SSL finishes, so
          // stopping at status alone leaves sslStatus stale.
          if (updated.status === "active" && updated.sslStatus === "active") {
            setAutoCheckingDomains((prev) => {
              const next = new Set(prev);
              next.delete(domainId);
              return next;
            });
            toast.success(`${updated.domain} is now active.`);
          }
        } catch (error) {
          // Silently fail, user can manually verify
        }
      }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, [autoCheckingDomains, projectId]);

  const loadDomains = async () => {
    try {
      setLoading(true);
      const data = await customDomainsApi.list(projectId);
      setDomains(data);
      // Auto-poll domains whose hostname is active but SSL is still provisioning,
      // so they resolve to "Active" without the user clicking Refresh.
      const provisioning = data
        .filter((d) => d.status === "active" && d.sslStatus !== "active")
        .map((d) => d.id);
      if (provisioning.length > 0) {
        setAutoCheckingDomains((prev) => {
          const next = new Set(prev);
          provisioning.forEach((id) => next.add(id));
          return next;
        });
      }
    } catch {
      toast.error("Failed to load custom domains");
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async () => {
    const { value, error } = validateDomain(newDomain);
    if (error) {
      setAddError(error);
      return;
    }
    setAddError(null);
    setAdding(true);
    try {
      const domain = await customDomainsApi.create(projectId, value);
      setDomains([domain, ...domains]);
      setNewDomain("");
      toast.success("Domain added! Add the DNS records below.");
      onDomainsChange?.();
    } catch (error: any) {
      setAddError(error.message || "Failed to add domain");
    } finally {
      setAdding(false);
    }
  };

  const handleVerify = async (domainId: string) => {
    setVerifyingDomains((prev) => new Set(prev).add(domainId));
    try {
      const updated = await customDomainsApi.verify(projectId, domainId);
      setDomains(domains.map((d) => (d.id === domainId ? updated : d)));

      if (updated.status === "active" && updated.sslStatus === "active") {
        toast.success("Domain verified successfully!");
        setAutoCheckingDomains((prev) => {
          const next = new Set(prev);
          next.delete(domainId);
          return next;
        });
      } else if (updated.status === "failed") {
        toast.error("Verification failed. Check DNS records.");
        setAutoCheckingDomains((prev) => {
          const next = new Set(prev);
          next.delete(domainId);
          return next;
        });
      } else {
        // Start auto-checking
        setAutoCheckingDomains((prev) => new Set(prev).add(domainId));
        toast("Checking DNS records... We'll notify you when ready.");
      }
    } catch {
      toast.error("Failed to verify domain");
    } finally {
      setVerifyingDomains((prev) => {
        const next = new Set(prev);
        next.delete(domainId);
        return next;
      });
    }
  };

  // Launch Entri's one-click DNS connector for a pending domain (via useEntri).
  // The hook hides our dialog while Entri is up and restores it on close; its
  // onSuccess kicks the existing verify + auto-poll. Rejects (e.g. SDK failed to
  // load) propagate so the caller can reveal the manual DNS fallback.
  const handleConnectEntri = async (domain: CustomDomain): Promise<void> => {
    if (
      !entriConfig?.enabled ||
      !entriConfig.applicationId ||
      !entriConfig.token
    ) {
      throw new Error("Entri is not available");
    }

    analytics.track(ENTRI_EVENTS.CONNECT_OPENED, {
      projectId,
      domain: domain.domain,
    });

    connectingDomainRef.current = domain;
    await openEntri({
      applicationId: entriConfig.applicationId,
      token: entriConfig.token,
      prefilledDomain: domain.domain,
      dnsRecords: toEntriRecords(domain.domain, domain.dnsRecords),
      userId: domain.id, // echoed back in Entri webhooks for correlation
    });
  };

  const stopAutoCheck = (domainId: string) => {
    setAutoCheckingDomains((prev) => {
      const next = new Set(prev);
      next.delete(domainId);
      return next;
    });
    toast("Auto-check stopped. You can verify manually anytime.");
  };

  // Promote a domain to Primary. Exactly one domain is Primary at a time, so the
  // previously-primary one is demoted back to Active automatically (the backend
  // unsets it in KV; we mirror that in local state).
  const handleSetPrimary = async (domainId: string) => {
    setSettingPrimary(domainId);
    try {
      await customDomainsApi.setPrimary(projectId, domainId);
      setDomains((prev) =>
        prev.map((d) => ({ ...d, isPrimary: d.id === domainId })),
      );
      toast.success(
        "Primary domain set! Your project is now live on this domain.",
      );
      onDomainsChange?.();
    } catch (error: any) {
      toast.error(error.message || "Failed to set primary domain");
    } finally {
      setSettingPrimary(null);
    }
  };

  const handleDelete = async (domainId: string) => {
    const target = domains.find((d) => d.id === domainId);
    const wasPrimary = target?.isPrimary === true;

    setDeletingDomains((prev) => new Set(prev).add(domainId));
    try {
      await customDomainsApi.delete(projectId, domainId);
      const remaining = domains.filter((d) => d.id !== domainId);
      setDomains(remaining);
      setAutoCheckingDomains((prev) => {
        const next = new Set(prev);
        next.delete(domainId);
        return next;
      });
      setEntriDomains((prev) => {
        const next = new Set(prev);
        next.delete(domainId);
        return next;
      });
      toast.success("Domain removed");

      // Never leave the project without a Primary: if we just deleted the live
      // domain and another active+SSL domain remains, promote it automatically.
      if (wasPrimary) {
        const next = remaining.find(
          (d) => d.status === "active" && d.sslStatus === "active",
        );
        if (next) {
          try {
            await customDomainsApi.setPrimary(projectId, next.id);
            setDomains(
              remaining.map((d) => ({ ...d, isPrimary: d.id === next.id })),
            );
            toast.success(`${next.domain} is now your primary domain.`);
          } catch {
            // Non-fatal — user can set a primary manually.
          }
        }
      }

      onDomainsChange?.();
    } catch {
      toast.error("Failed to remove domain");
    } finally {
      setDeletingDomains((prev) => {
        const next = new Set(prev);
        next.delete(domainId);
        return next;
      });
    }
  };

  return (
    <Dialog open={isOpen && !entriActive} onOpenChange={onClose}>
      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-[560px]">
        <DialogHeader className="space-y-1 px-6 pb-5 pt-6 text-left">
          <DialogTitle className="text-base font-semibold tracking-tight">
            Custom domains
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Connect a domain you own to this project.
          </DialogDescription>
        </DialogHeader>

        {/* Domain list */}
        <div className="max-h-[56vh] overflow-y-auto px-6 pb-1">
          {loading ? (
            <div className="flex items-center justify-center py-14">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : domains.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center">
              <Globe className="h-7 w-7 text-muted-foreground/50" />
              <h3 className="mt-3 text-sm font-medium text-foreground">
                No domains yet
              </h3>
              <p className="mt-1 max-w-[20rem] text-sm text-muted-foreground">
                Add a subdomain you own below to reach this project on your own
                URL.
              </p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {domains.map((domain) => (
                <DomainCard
                  key={domain.id}
                  domain={domain}
                  isAutoChecking={autoCheckingDomains.has(domain.id)}
                  viaEntri={entriDomains.has(domain.id)}
                  isVerifying={verifyingDomains.has(domain.id)}
                  isDeleting={deletingDomains.has(domain.id)}
                  isSettingPrimary={settingPrimary === domain.id}
                  entriEnabled={entriEnabled}
                  onConnectEntri={() => handleConnectEntri(domain)}
                  onVerify={() => handleVerify(domain.id)}
                  onStopAutoCheck={() => stopAutoCheck(domain.id)}
                  onSetPrimary={() => handleSetPrimary(domain.id)}
                  onDelete={() => handleDelete(domain.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Add domain */}
        <div className="border-t border-border px-6 py-4">
          <div className="flex items-center gap-2">
            <Input
              type="text"
              value={newDomain}
              onChange={(e) => {
                setNewDomain(e.target.value);
                if (addError) setAddError(null);
              }}
              placeholder="app.example.com"
              className={cn(
                "h-9 flex-1 font-mono",
                addError && "border-destructive focus-visible:ring-destructive",
              )}
              onKeyDown={(e) => e.key === "Enter" && handleAddDomain()}
            />
            <Button
              onClick={handleAddDomain}
              disabled={adding || !newDomain.trim()}
              className="h-9 px-5"
            >
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
            </Button>
          </div>
          {addError ? (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-destructive">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              {addError}
            </p>
          ) : (
            <p className="mt-2 text-xs text-muted-foreground">
              Enter a subdomain you own, e.g. app.example.com
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
